import { useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input } from '@/ui/Field';
import { EstadoError } from '@/components/EstadoError';
import { Icono } from './piezas';
import {
  useBorrarCampana, useBorrarValor, useCampanas, useCrearCampana, useCrearValor,
  useValores, type Campana, type Valor,
} from './api';

/**
 * Gestion.tsx — los dos paneles de `kudos:manage`: Valores y Campañas.
 *
 * ## El defecto que tenían los dos, y era el mismo
 *
 * Borrar llamaba a `Utils.confirm`, que **reutiliza `#modal-overlay`** — el
 * mismo elemento en el que estaba abierto el panel. La confirmación
 * sobrescribía el panel; al aceptar, `closeModal()` cerraba todo; y después
 * `_renderBadgeList()` buscaba su contenedor, no lo encontraba y se iba de
 * puntillas:
 *
 *     const el = document.getElementById('badge-list');
 *     if (!el) return;          // ← aquí moría, en silencio
 *
 * Resultado: borrar un valor cerraba el panel entero y la lista nunca se
 * refrescaba. No daba error; parecía que la ventana «se había cerrado sola».
 *
 * Aquí los diálogos se apilan porque cada uno es su propio elemento, y la lista
 * se repinta porque la mutación invalida su consulta. Ninguna de las dos cosas
 * hay que acordarse de hacerla.
 */

/* ── Valores corporativos ──────────────────────────────────────────────── */

export function PanelDeValores({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  // `abierto` como interruptor: el panel se monta siempre para que el diálogo
  // pueda animar su entrada, y sin esto pediría su lista al cargar la pantalla.
  const { data: valores = [], isLoading, isError, error, refetch } = useValores(abierto);
  const crear = useCrearValor();
  const borrar = useBorrarValor();

  const [nombre, setNombre] = useState('');
  const [icono, setIcono] = useState('bi-star');
  const [color, setColor] = useState('#F5A623');
  const [puntos, setPuntos] = useState(1);
  const [porBorrar, setPorBorrar] = useState<Valor | null>(null);

  function guardar() {
    const limpio = nombre.trim();
    if (!limpio) { Utils.toast('Escribe un nombre', 'warning'); return; }
    crear.mutate(
      { name: limpio, icon: icono || 'bi-star', color, points: puntos || 1 },
      {
        onSuccess: () => { setNombre(''); Utils.toast('Valor creado', 'success'); },
        onError: (e: unknown) => Utils.toast((e as Error)?.message || 'No se pudo crear', 'error'),
      },
    );
  }

  return (
    <>
      <Dialog
        abierto={abierto}
        onCambio={(v) => { if (!v) alCerrar(); }}
        titulo="Valores corporativos"
        descripcion="Con estos valores se reconoce a las personas."
        ancho="max-w-2xl"
        pie={<Button variant="ghost" onClick={alCerrar}>Cerrar</Button>}
      >
        <div className="mb-4 rounded-sm border border-line bg-sunken p-3.5">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Nombre" className="min-w-[10rem] flex-1">
              <Input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Servicio"
                onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
              />
            </Field>
            <Field label="Icono" className="min-w-[9rem] flex-1">
              <Input
                value={icono}
                onChange={(e) => setIcono(e.target.value)}
                placeholder="bi-star"
                aria-describedby="ayuda-icono"
              />
            </Field>
            <Field label="Color" className="w-20">
              <Input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 cursor-pointer p-0.5"
                aria-label="Color del valor"
              />
            </Field>
            <Field label="Puntos" className="w-20">
              <Input
                type="number"
                min={1}
                max={5}
                value={puntos}
                onChange={(e) => setPuntos(Number(e.target.value) || 1)}
              />
            </Field>
            <Button onClick={guardar} disabled={crear.isPending} className="mb-0.5">
              <i className="bi bi-plus-lg" aria-hidden="true" />
              {crear.isPending ? 'Creando…' : 'Añadir'}
            </Button>
          </div>
          <p id="ayuda-icono" className="mt-1.5 text-2 text-muted">
            El nombre del icono en Bootstrap Icons, por ejemplo <code>bi-star</code>.
          </p>
        </div>

        {isLoading && <p className="text-3 text-muted">Cargando…</p>}
        {isError && <EstadoError err={error} que="los valores" onReintentar={() => { refetch(); }} />}
        {!isLoading && !isError && !valores.length && (
          <p className="text-3 text-muted">Sin valores definidos.</p>
        )}
        <ul>
          {valores.map((v) => (
            <li
              key={v.id ?? v.key}
              className="mb-1.5 flex items-center gap-2.5 rounded-sm border border-line px-2.5 py-2"
            >
              <span className="text-6"><Icono nombre={v.icon} respaldo="bi-star" /></span>
              <span className="min-w-0 flex-1 truncate text-3 font-semibold">{v.name}</span>
              <span
                className="shrink-0 rounded-sm px-2 py-0.5 text-1 font-semibold"
                style={{ background: `${v.color || '#F5A623'}22`, color: v.color || undefined }}
              >
                {v.points || 1} pts
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Eliminar el valor ${v.name}`}
                onClick={() => setPorBorrar(v)}
                disabled={v.id == null}
              >
                <i className="bi bi-trash" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      </Dialog>

      {/* Se apila SOBRE el panel, que sigue abierto detrás. Es la diferencia
          con `Utils.confirm`, que reutilizaba el mismo velo y lo tapaba. */}
      <ConfirmDialog
        abierto={!!porBorrar}
        onCambio={(v) => { if (!v) setPorBorrar(null); }}
        titulo="Eliminar el valor"
        descripcion={
          porBorrar
            ? `Se elimina «${porBorrar.name}». Los reconocimientos que ya lo usan lo conservan.`
            : ''
        }
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!porBorrar?.id) return;
          borrar.mutate(porBorrar.id, {
            onSuccess: () => { setPorBorrar(null); Utils.toast('Valor eliminado', 'success'); },
            onError: (e: unknown) =>
              Utils.toast((e as Error)?.message || 'No se pudo eliminar', 'error'),
          });
        }}
      />
    </>
  );
}

/* ── Campañas ──────────────────────────────────────────────────────────── */

export function PanelDeCampanas({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  const { data: campanas = [], isLoading, isError, error, refetch } = useCampanas(true, abierto);
  const crear = useCrearCampana();
  const borrar = useBorrarCampana();

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [icono, setIcono] = useState('bi-bullseye');
  const [porBorrar, setPorBorrar] = useState<Campana | null>(null);

  // Un rango al revés no lo rechaza el servidor y la campaña queda invisible:
  // `running` nunca es cierto, así que no sale en el banner y quien la creó
  // cree que no se guardó.
  const rangoAlReves = !!desde && !!hasta && desde > hasta;

  function guardar() {
    const limpio = titulo.trim();
    if (!limpio) { Utils.toast('Escribe un título', 'warning'); return; }
    if (rangoAlReves) { Utils.toast('La fecha de fin es anterior a la de inicio', 'error'); return; }
    crear.mutate(
      {
        title: limpio,
        description: descripcion.trim() || null,
        start_at: desde || null,
        end_at: hasta || null,
        icon: icono || 'bi-bullseye',
      },
      {
        onSuccess: () => {
          setTitulo(''); setDescripcion(''); setDesde(''); setHasta('');
          Utils.toast('Campaña creada', 'success');
        },
        onError: (e: unknown) => Utils.toast((e as Error)?.message || 'No se pudo crear', 'error'),
      },
    );
  }

  return (
    <>
      <Dialog
        abierto={abierto}
        onCambio={(v) => { if (!v) alCerrar(); }}
        titulo="Campañas de reconocimiento"
        descripcion="Una campaña destaca un motivo durante un periodo."
        ancho="max-w-2xl"
        pie={<Button variant="ghost" onClick={alCerrar}>Cerrar</Button>}
      >
        <div className="mb-4 rounded-sm border border-line bg-sunken p-3.5">
          <Field label="Título" className="mb-2">
            <Input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej: Mes del Servicio"
            />
          </Field>
          <Field label="Descripción" opcional className="mb-2">
            <Input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Breve descripción"
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Field label="Desde" opcional className="min-w-[8rem] flex-1">
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
            </Field>
            <Field
              label="Hasta"
              opcional
              className="min-w-[8rem] flex-1"
              error={rangoAlReves ? 'Es anterior a la fecha de inicio.' : undefined}
            >
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </Field>
            <Field label="Icono" className="min-w-[8rem] flex-1">
              <Input
                value={icono}
                onChange={(e) => setIcono(e.target.value)}
                placeholder="bi-bullseye"
              />
            </Field>
          </div>
          <Button
            onClick={guardar}
            disabled={crear.isPending || rangoAlReves}
            className="mt-2.5"
          >
            <i className="bi bi-plus-lg" aria-hidden="true" />
            {crear.isPending ? 'Creando…' : 'Crear campaña'}
          </Button>
        </div>

        {isLoading && <p className="text-3 text-muted">Cargando…</p>}
        {isError && <EstadoError err={error} que="las campañas" onReintentar={() => { refetch(); }} />}
        {!isLoading && !isError && !campanas.length && (
          <p className="text-3 text-muted">Sin campañas.</p>
        )}
        <ul>
          {campanas.map((c) => (
            <li
              key={c.id}
              className="mb-1.5 flex items-center gap-2.5 rounded-sm border border-line px-2.5 py-2"
            >
              <span className="text-6"><Icono nombre={c.icon} respaldo="bi-bullseye" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-3 font-semibold">
                  <span className="truncate">{c.title}</span>
                  {c.running && (
                    <span className="shrink-0 rounded-sm bg-ok-ink/12 px-1.5 py-0.5 text-1 text-ok-ink">
                      activa
                    </span>
                  )}
                </div>
                <div className="text-1 text-muted">
                  {c.start_at || '—'} → {c.end_at || '—'}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Eliminar la campaña ${c.title}`}
                onClick={() => setPorBorrar(c)}
              >
                <i className="bi bi-trash" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      </Dialog>

      <ConfirmDialog
        abierto={!!porBorrar}
        onCambio={(v) => { if (!v) setPorBorrar(null); }}
        titulo="Eliminar la campaña"
        descripcion={porBorrar ? `Se elimina «${porBorrar.title}».` : ''}
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!porBorrar) return;
          borrar.mutate(porBorrar.id, {
            onSuccess: () => { setPorBorrar(null); Utils.toast('Campaña eliminada', 'success'); },
            onError: (e: unknown) =>
              Utils.toast((e as Error)?.message || 'No se pudo eliminar', 'error'),
          });
        }}
      />
    </>
  );
}
